import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Protect edits across workspace navigation, including keyboard navigation.
// Autosubmitting controls handle their own save cycle and are excluded.
export default class extends Controller {
  static values = { message: String }
  static targets = ["dialog"]

  connect() {
    this.baselines = new Map()
    this.pending = new Map()
    this.allowSubmit = new WeakSet()
    this.discardOnSubmit = new WeakSet()
    this.element.querySelectorAll("form").forEach((form) => this.track(form))
    this.onFocus = (event) => this.track(event.target.closest("form"))
    this.onVisit = (event) => {
      if (!this.dirtyForms().length) return
      event.preventDefault()
      this.ask(() => { this.baselines.clear(); Turbo.visit(event.detail.url) })
    }
    this.onUnload = (event) => {
      if (this.dirtyForms().length) { event.preventDefault(); event.returnValue = "" }
    }
    this.onSubmit = (event) => {
      const form = event.target
      const replay = this.allowSubmit.delete(form)
      this.discardOnSubmit.delete(form)
      const others = this.dirtyForms().filter((dirty) => dirty !== form)
      if (others.length && !replay) {
        event.preventDefault()
        event.stopImmediatePropagation()
        const submitter = event.submitter
        this.ask(() => { this.allowSubmit.add(form); submitter ? form.requestSubmit(submitter) : form.requestSubmit() })
      } else if (others.length) {
        this.discardOnSubmit.add(form)
      }
    }
    this.onSubmitStart = (event) => {
      const form = event.target
      if (this.baselines.has(form)) this.pending.set(form, this.baselines.get(form))
      if (this.discardOnSubmit.has(form)) { this.baselines.clear(); this.discardOnSubmit.delete(form) }
      this.baselines.delete(form)
    }
    this.onSubmitEnd = (event) => {
      const form = event.target
      if (!event.detail.success && this.pending.has(form)) this.baselines.set(form, this.pending.get(form))
      else this.track(form)
      this.pending.delete(form)
    }
    // Turbo ends a submission before its redirect renders, so a form kept in
    // place by a morph refresh was measured with the typed values still in it;
    // measure every form again once the morph has put the saved values back.
    this.onMorph = () => {
      this.baselines.clear()
      this.element.querySelectorAll("form").forEach((form) => this.track(form))
    }
    this.element.addEventListener("focusin", this.onFocus)
    this.element.addEventListener("submit", this.onSubmit, true)
    this.element.addEventListener("turbo:submit-start", this.onSubmitStart)
    this.element.addEventListener("turbo:submit-end", this.onSubmitEnd)
    document.addEventListener("turbo:before-visit", this.onVisit)
    document.addEventListener("turbo:morph", this.onMorph)
    window.addEventListener("beforeunload", this.onUnload)
  }

  disconnect() {
    this.element.removeEventListener("focusin", this.onFocus)
    this.element.removeEventListener("submit", this.onSubmit, true)
    this.element.removeEventListener("turbo:submit-start", this.onSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this.onSubmitEnd)
    document.removeEventListener("turbo:before-visit", this.onVisit)
    document.removeEventListener("turbo:morph", this.onMorph)
    window.removeEventListener("beforeunload", this.onUnload)
  }

  track(form) {
    if (!form || this.baselines.has(form) || form.method.toLowerCase() === "get") return
    if (form.querySelector('[data-controller~="autosubmit"]')) return
    if (!form.querySelector('input:not([type="hidden"]):not([type="submit"]), select, textarea, #eval-matrix, #fk-scorer')) return
    this.baselines.set(form, this.snapshot(form))
  }

  snapshot(form) {
    return JSON.stringify([...new FormData(form)].filter(([key]) => !["authenticity_token", "utf8"].includes(key))
      .map(([key, value]) => {
        if (value instanceof File) return [key, [value.name, value.size, value.name ? value.lastModified : 0]]
        // Rails decimals and the scoring widgets format equivalent factors differently.
        if (/\[(?:residual_)?(?:probability|exposure|severity)\]$/.test(key) && value.trim() && Number.isFinite(Number(value))) value = String(Number(value))
        return [key, value]
      }))
  }

  dirtyForms() {
    return [...this.baselines].filter(([form, baseline]) => form.isConnected && this.snapshot(form) !== baseline).map(([form]) => form)
  }

  ask(callback) {
    this.leaveAction = callback
    if (!this.dialogTarget.open) this.dialogTarget.showModal()
  }

  stay() { this.dialogTarget.close(); this.leaveAction = null }

  discard() {
    const action = this.leaveAction
    this.stay()
    action?.()
  }
}
